import axios from 'axios'
import config from '../../js/conf/config'
import store from '@/store'
import router from '@/router'

let refreshFlight = null
let lastRefresh = null
let loggedOutSession = null

function sessionSnapshot() {
	return {
		token: localStorage.getItem(config.tokenKey),
		refreshToken: localStorage.getItem(config.refreshTokenKey)
	}
}

function sameSession(left, right) {
	return !!left && !!right && left.token === right.token && left.refreshToken === right.refreshToken
}

function requestError(kind, message, response, code) {
	const body = response && response.data
	const headers = response && response.headers
	const error = new Error(message)
	error.kind = kind
	error.httpStatus = response ? response.status : null
	error.businessCode = body && typeof body === 'object' ? body.code : null
	error.requestId = headers ? headers['request-id'] || headers['Request-Id'] || null : null
	if (code) error.code = code
	// Preserve the response fields used by existing callers without retaining credentials/config.
	if (response) {
		error.response = {
			status: response.status,
			headers: { 'request-id': error.requestId },
			data: { code: error.businessCode, message, msg: message }
		}
	}
	return error
}

function transportError(error) {
	if (error.kind) return error
	if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
		return requestError('timeout', '请求超时，请手动重试', error.response, error.code)
	}
	if (!error.response) return requestError('network', '网络连接失败，请检查网络后重试', null, error.code)
	const status = error.response.status
	const message = status >= 500 ? '服务暂时不可用，请稍后重试'
		: status === 403 ? '没有权限访问该资源'
		: status === 429 ? '请求过于频繁，请稍后重试'
		: status === 401 ? '登录认证未通过'
		: '请求失败，请稍后重试'
	return requestError(status === 401 ? 'auth' : 'http', message, error.response, error.code)
}

function responseBody(response) {
	const body = response.data
	// Legacy business APIs use status instead of code; keep their wire shape unchanged.
	const resultCode = body && (body.code === undefined ? body.status : body.code)
	if (!body || typeof body !== 'object' || Array.isArray(body)
		|| !((typeof resultCode === 'number' && Number.isInteger(resultCode))
			|| (typeof resultCode === 'string' && /^-?\d+$/.test(resultCode)))) {
		throw requestError('invalid_response', '服务响应格式异常，请稍后重试', response)
	}
	return Object.assign({}, body, { msg: body.msg || body.message })
}

function sessionChanged() {
	return requestError('session_changed', '登录状态已变更，请重新查询')
}

function expireSession(session, error) {
	if (sameSession(sessionSnapshot(), session) && !sameSession(loggedOutSession, session)) {
		loggedOutSession = session
		store.commit('login/OUT_LOGIN')
		const navigation = router.push('/')
		if (navigation && navigation.catch) {
			navigation.catch(failure => {
				if (!failure || !failure._isRouter) console.error('登录页跳转失败')
			})
		}
	}
	return error
}

async function refreshToken(session) {
	if (!session.refreshToken) {
		throw expireSession(session, requestError('auth', '登录已失效，请重新登录'))
	}
	try {
		const response = await axios.post(config.baseApi + '/merchant/passport/refresh', {}, {
			headers: { Authorization: `Bearer ${session.refreshToken}` },
			timeout: 15000
		})
		if (!sameSession(sessionSnapshot(), session)) throw sessionChanged()
		const body = responseBody(response)
		if (Number(body.code) === 401 || Number(body.code) === 403) {
			throw expireSession(session, requestError('auth', '登录已失效，请重新登录', response))
		}
		if (Number(body.code) !== 200) {
			throw requestError('auth_refresh', '登录认证暂时失败，请手动重试', response)
		}
		const data = body.data
		if (!data || typeof data.access_token !== 'string' || !data.access_token.trim()
			|| typeof data.refresh_token !== 'string' || !data.refresh_token.trim()
			|| !['number', 'string'].includes(typeof data.expire_at)
			|| !Number.isFinite(Number(data.expire_at)) || Number(data.expire_at) <= 0) {
			throw requestError('invalid_response', '登录认证响应格式异常，请手动重试', response)
		}
		store.commit('login/SET_TOKEN', {
			token: data.access_token,
			re_token: data.refresh_token,
			token_ex: Date.now() + Number(data.expire_at) * 1000
		})
		const nextSession = sessionSnapshot()
		lastRefresh = { previous: session, current: nextSession }
		return nextSession
	} catch (failure) {
		// An old refresh must never clear or overwrite a newer login session.
		if (failure.kind === 'auth') throw failure
		if (!sameSession(sessionSnapshot(), session)) throw sessionChanged()
		if (failure.response && [401, 403].includes(failure.response.status)) {
			throw expireSession(session, requestError('auth', '登录已失效，请重新登录', failure.response))
		}
		const error = transportError(failure)
		error.phase = 'auth_refresh'
		throw error
	}
}

function freshSession(session) {
	const current = sessionSnapshot()
	if (!sameSession(current, session)) {
		if (lastRefresh && sameSession(lastRefresh.previous, session) && sameSession(lastRefresh.current, current)) {
			return Promise.resolve(current)
		}
		return Promise.reject(sessionChanged())
	}
	if (!refreshFlight || !sameSession(refreshFlight.session, session)) {
		const flight = { session }
		flight.promise = refreshToken(session).finally(() => {
			if (refreshFlight === flight) refreshFlight = null
		})
		refreshFlight = flight
	}
	return refreshFlight.promise
}

function skipsAuthRefresh(url) {
	return /\/merchant\/passport\/(login|smsLogin|register|refresh)\/?(?:[?#]|$)/.test(url)
}

async function send(httpConfig, session, retried = false) {
	let response
	try {
		response = await axios(httpConfig)
	} catch (failure) {
		if (!failure.response || failure.response.status !== 401 || skipsAuthRefresh(httpConfig.url)) {
			throw transportError(failure)
		}
		response = failure.response
	}
	if (skipsAuthRefresh(httpConfig.url)) return responseBody(response)
	if (response.status === 401 || (response.data && Number(response.data.code) === 401)) {
		const error = requestError('auth', '登录已失效，请重新登录', response)
		if (retried) throw expireSession(session, error)
		const nextSession = await freshSession(session)
		if (!sameSession(sessionSnapshot(), nextSession)) throw sessionChanged()
		return send(Object.assign({}, httpConfig, {
			headers: Object.assign({}, httpConfig.headers, { Authorization: `Bearer ${nextSession.token}` })
		}), nextSession, true)
	}
	return responseBody(response)
}

function httpClient(url, params = {}, method = 'get', customHeaders = {}) {
	const session = sessionSnapshot()
	const httpConfig = {
		url,
		method,
		headers: Object.assign({ Authorization: `Bearer ${session.token}` }, customHeaders)
	}
	if (method === 'get') httpConfig.params = params
	else httpConfig.data = params
	return send(httpConfig, session)
}

export default {
	get: (url, data = {}) => httpClient(url, data, 'get'),
	post: (url, data = {}) => httpClient(url, data, 'post'),
	put: (url, data = {}) => httpClient(url, data, 'put'),
	patch: (url, data = {}) => httpClient(url, data, 'patch'),
	delete: (url, data) => httpClient(url, data, 'delete'),
	postJson: (url, data = {}, customHeaders = {}) => httpClient(url, data, 'post', {
		'Content-Type': 'application/json',
		...customHeaders
	})
}
